/* WL SHOT MAP  (was: FDS STABILITY GRID)
   ============================================================================
   Read-only view over the chart's state. Decoupled from the chart core so the
   50KB engine does not have to be rewritten. Bag state is read from the URL
   hash (authoritative) or the rendered slots (first paint). Adding a disc is
   done by writing the hash, which the core already listens for.

   WHAT CHANGED, AND WHY IT HAD TO
   -------------------------------
   The old grid plotted X = turn + fade. That disagreed with the fairway view
   directly above it in two separate ways, and both were measured:

     1. It weighted turn and fade EQUALLY. The chart does not -- it uses
        0.042*turn + 0.029*fade. Turn matters about 45% more. The grid was
        drawing a different disc from the one the chart drew.

     2. It ignored flight length entirely. A distance driver and a putter both
        rated turn+fade 3 landed in the same column, despite finishing 29 ft
        and 20 ft offline respectively. Same cell, completely different shot.

   X is now LATERAL FEET, computed with the chart's own geometry. Same inputs,
   same constants, so the two views cannot drift apart again.

   THE FUNNEL
   ----------
   A putter cannot finish 45 ft offline. It does not fly far enough to get
   there. So the rows are not all the same width -- measured across the whole
   spine at the 95th percentile of |lateral offset|:

        Distance    +/- 47 ft   100%
        Fairway     +/- 42 ft    90%
        Midrange    +/- 34 ft    72%
        Putt & App  +/- 23 ft    50%

   A column is the SAME distance offline in every row -- that is what makes
   this a funnel and not four unrelated rows. At a column width of 17.7 ft the
   rows earn 5 / 5 / 5 / 3 = 18 cells.

   THE MIDRANGE ROW WAS WRONG UNTIL 22 AUG. It was drawn 3 wide, which put it
   level with the putter row -- so the map said a Zone and an Aviar finish the
   same distance offline, which anybody who has thrown both knows is false.
   The table above already said otherwise: 34 ft is 1.9 columns and rounds to
   2, 23 ft is 1.3 and rounds to 1. Rounding every mould in the spine agrees --
   59 midranges (15% of the band) genuinely land in the outer column against 7
   putters (1.8%, a rounding tail). The row is 5 wide now and the funnel has
   one taper, across the putters, where the data actually puts one.

   Column counts are ODD by construction so every row keeps a centre STRAIGHT
   column; an even count would put a boundary exactly where "dead straight"
   should be, which is the one place a disc golfer actually aims.

   NOTE FOR WHOEVER TOUCHES THIS NEXT: 17.7 sits inside the 17.1-21.2 range
   that yields this shape, and was picked within it for the best balance
   (6.7x rather than 14.3x) and the most brands able to show a COMPLETE bag
   (9 of 12 rather than 6). An earlier commit sat at 21.2 -- the exact cliff
   edge, where one more wide-fairway mould would have silently reshaped the
   whole map to 14 cells. If the dataset grows a lot, re-run the sweep.
   verify-funnel.mjs asserts the 5/5/5/3 shape, so drift fails the suite
   rather than shipping quietly.

   The cells outside a row's reach are not drawn. They are not empty, they are
   unreachable.

   FOREHANDS
   ---------
   Shape is computed PER BAG SLOT, not per mould. RHFH and LHBH mirror the
   offset. So an overstable driver covers the far-left column backhand and the
   far-right column forehand -- one mould, two cells -- and a left-hander
   finally sees a map that agrees with the fairway above it.

   CLUSTERING
   ----------
   Across the spine, 82% of moulds shared a coordinate on the old axis, worst
   pile-up 26. The new axis alone cuts that to 60% and worst 10, because
   multiplying by flight length separates discs that merely shared a rating.
   Coincident moulds are then drawn as ONE marker carrying a count. 693 moulds
   become ~213 markers. Clicking one opens the full list -- because zooming a
   stack of identical points just gives you a bigger stack.
   ========================================================================= */
(function () {
"use strict";
var FD = window.__FDS__; if (!FD || !FD.molds) return;
var M = FD.molds;

/* The one piece of view state this module owns. "" means every brand. It is
   deliberately NOT in the URL hash: the hash is the shareable bag, and a
   brand you flicked through while browsing is not part of what you meant to
   send someone. */
var brand = "";

/* ---- geometry, lifted from the chart core so the two cannot drift ---- */
var TURN_W = 0.042, FADE_W = 0.029, DIST_0 = 175.1, DIST_K = 1.152;
/* GLIDE DAMPS THE FADE -- MEASURED AGAINST THE SPEED. Same constants and the
   same reasoning as chart.js's GM(); the long note lives there. Short version:
   Innova rates the Gator and the Roc3 identically at 0 turn / 3 fade, so glide
   was the only thing left to tell them apart, and it was pulling the wrong
   way. What counts is not how much glide a disc has but how much it has FOR
   ITS SPEED -- median glide is 5 at speed 5 and up, 3 below. A Gator carries 2
   where its class carries 5 and dumps accordingly; an XCaliber carries the
   normal 5 and does not move.

   These two files must agree to the decimal. If you change one, change the
   other -- verify-funnel.mjs and the golden path snapshot both fail loudly if
   the map and the fairway disagree, which is exactly what they are for. */
var GLIDE_LO = 2, GLIDE_HI = 7, GLIDE_K = 0.5;
function expectedGlide(s) { return s >= 5 ? 5 : 3; }
function glideMul(s, g) {
  var v = g < GLIDE_LO ? GLIDE_LO : g > GLIDE_HI ? GLIDE_HI : g;
  return Math.pow(expectedGlide(s) / v, GLIDE_K);
}
function flightFt(d) { return DIST_0 + DIST_K * (10 * d.s + 6 * d.g); }
function lateralFt(d, mir) {
  return (mir || 1) * -(TURN_W * d.tu + FADE_W * d.f * glideMul(d.s, d.g)) * flightFt(d);
}

var COL_FT = 17.7;
var BANDS = [
  { k: "DD", lbl: "Distance",        sub: "Speed 10-14", lo: 10, hi: 14, cols: 5 },
  { k: "FD", lbl: "Fairway",         sub: "Speed 6-9",   lo: 6,  hi: 9,  cols: 5 },
  { k: "MR", lbl: "Midrange",        sub: "Speed 4-5",   lo: 4,  hi: 5,  cols: 5 },
  { k: "PA", lbl: "Putt & Approach", sub: "Speed 1-4, glide 1-3", lo: 1, hi: 4, cols: 3 }
];
/* WIDTHS COME FROM THE DATA, NOT FROM THE DRAWING.
   These were 5/5/3/3, which drew a putter as reaching exactly as far offline
   as a midrange. That is wrong on the numbers and it is wrong on the course.
   Rounding lateralFt into 17.7 ft columns across all 1,614 moulds gives:

     Distance   +/-4 columns   (38 moulds past +/-2)
     Fairway    +/-3           (6 past +/-2)
     Midrange   +/-2           (59 moulds, 15% of the band, genuinely reach it)
     Putt & App +/-2           (7 moulds, 1.8% -- a rounding tail, not a shape)

   So the midrange row earns its outer column and the putter row does not.
   5/5/5/3 clamps 44 discs in the two fast bands, same as before, and now
   clamps 7 putters instead of pretending 59 midranges finish inside 26 ft. */
var BAND = {}; BANDS.forEach(function (b) { BAND[b.k] = b; });

/* PLAIN DIRECTIONS. An earlier version labelled these "off-side" and "over"
   so the words would stay true for a left-hander without the map having to
   redraw. That bought correctness with a word nobody in disc golf actually
   says, which is a bad trade -- a beginner cannot guess it and an experienced
   player has never used it.

   These labels are FIXED TO THE SCREEN and the dots move instead. The whole
   map mirrors with the throw selector exactly as the fairway view above it
   does, so left is always left and right is always right, for everybody, in
   words that need no explaining. */
var COLLBL = { "-2": "GOES HARD LEFT", "-1": "FINISHES LEFT", "0": "STRAIGHT",
               "1": "FINISHES RIGHT", "2": "GOES HARD RIGHT" };
var COLPHRASE = { "-2": "hard left", "-1": "left", "0": "straight",
                  "1": "right", "2": "hard right" };

function colOf(b, ft) {
  var half = (b.cols - 1) / 2;
  var i = Math.round(ft / COL_FT);
  if (i < -half) i = -half; if (i > half) i = half;
  return i;
}
function colX(b, i) {                    /* signed column index -> svg x */
  return MIDX + i * COLW;
}

var THROWS = { RHBH: 1, LHFH: 1, RHFH: -1, LHBH: -1 };
function mirrorOf(t) { return THROWS[t] || 1; }

/* ---- layout ---- */
var X0 = 168, X1 = 986, ROWH = 116, TOP = 58, PAD = 20;
var MIDX = (X0 + X1) / 2, COLW = (X1 - X0) / 5;

function slug(d) { return (d.v + "-" + d.m).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
function b1(n) { return (Math.round(10 * n) / 10).toString(); }
function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
function el(t, a) {
  var n = document.createElementNS("http://www.w3.org/2000/svg", t);
  for (var k in a) if (a[k] !== null && a[k] !== void 0) n.setAttribute(k, a[k]);
  return n;
}
/* BAND IS A FUNCTION OF SPEED. NOTHING ELSE. Rewritten 7 Sep 2026.
   It used to prefer the mould's own type label and fall back to speed, which
   made the row a property of whoever typed the label rather than of the disc.
   60 of 1,614 moulds carried a label that contradicted the speed rule, and the
   damage landed exactly where this tool is supposed to be strongest: a Firebird
   (9/3/0/4, labelled FD) and a Discraft Raptor (9/4/0/3, labelled DD) drew in
   different rows despite being the same disc in two plastics. The cross-brand
   equivalence engine inherited the same gate and reported that "Discraft has no
   Firebird" -- it has three.

   Ryan's ruling, 7 Sep: FAIRWAY DRIVERS STOP AT 9. So distance starts at 10,
   and speed 9 -- Firebird, Raptor, Captain's Raptor, Predator, Valkyrie -- is a
   fairway row. 167 moulds moved; the fairway band went 269 -> 411.

   The label still exists in the data and still drives the catalogue's Type
   filter copy, but it no longer decides where a disc is drawn or what it is
   compared against. One rule, every brand. */
/* SPEED 4 IS TWO DIFFERENT DISCS, AND GLIDE IS WHAT SEPARATES THEM. Added
   7 Sep 2026, after Ryan: "zone should be putter."

   A Zone (4/3/0/3) and a Roc (4/5/0/2) share a speed and do completely
   different jobs. Speed alone put both in the midrange row. Glide is the
   split that already exists in the numbers: approach discs are built to drop
   -- Zone 3, Harp 3, Pig 1, Tactic 2, Prodigy A-series 2-3 -- while midranges
   are built to carry: Roc 5, Shark 4, Cobra 5, Buzzz 4, Mako3 5.

   So at speed 4 only, glide 3 or less is putt & approach. 64 moulds moved,
   and the list reads like a shelf of approach discs: Zone, Zone GT, Zone OS,
   Tactic, P4, Drop, Maestro, Pig, Rat, Alien, Toro, Entropy, Suspect,
   Culprit, A1-A4. Nothing above speed 4 is touched -- a Wasp (5/3) and a
   Gator (5/2) are overstable MIDRANGES and stay in the midrange row. */
/* AND A VARIANT IS WHATEVER ITS BASE MOULD IS. Ryan, 7 Sep: "zone ss is a
   putter." He is right and the numbers cannot see it -- Zone SS is 4/4/-1/2
   and a Shark is 4/4/0/2, so no glide or fade cut separates them. What does
   separate them is that one is a Zone. A variant carries its family's job:
   Zone SS, Zone GT and Zone OS are all Zones, Buzzz OS is still a Buzzz.
   Matched on the first word of the name within the same brand, and only at
   speed 4-5 where the PA/MR line is genuinely ambiguous -- it changes exactly
   one mould in the current 1,614 and will catch the next Zone variant for
   free. Nothing above speed 5 consults it. */
function baseBand(d) {
  var sp = d.m.indexOf(" ");
  if (sp < 1 || d.s < 4 || d.s > 5) return null;
  var b = BY[slug({ v: d.v, m: d.m.slice(0, sp) })];
  return b ? rawBand(b) : null;
}
function rawBand(d) {
  return d.s >= 10 ? "DD"
       : d.s >= 6  ? "FD"
       : d.s >= 4  ? (d.s === 4 && d.g <= 3 ? "PA" : "MR")
       : "PA";
}
function bandOf(d) { return baseBand(d) || rawBand(d); }

var BY = {}; M.forEach(function (d) { BY[slug(d)] = d; });
function base(tok) { var i = tok.indexOf("~"); return i < 0 ? tok : tok.slice(0, i); }
function thrOf(tok) { var i = tok.indexOf("~"); return i < 0 ? null : tok.slice(i + 1); }

function globalThrow() {
  var m = /(?:^|#|&)t=([^&]+)/.exec(location.hash);
  return m ? decodeURIComponent(m[1]) : "RHBH";
}

/* Bag as SLOTS, not moulds. A mould bagged twice with different throws is two
   slots and lights two cells -- that is the whole point of the forehand fix. */
function bagSlots() {
  var g = globalThrow();
  var m = /(?:^|#|&)d=([^&]+)/.exec(location.hash);
  if (m) {
    return decodeURIComponent(m[1]).split(",").filter(function (s) { return BY[base(s)]; })
      .map(function (tok) { return { d: BY[base(tok)], thr: thrOf(tok) || g, tok: tok }; });
  }
  var out = [];
  Array.prototype.forEach.call(document.querySelectorAll("#fdsSlots .fdsfc-slot"), function (row) {
    var nm = row.querySelector(".fdsfc-slot__name"); if (!nm) return;
    var numEl = row.querySelector(".fdsfc-slot__n"),
        want = numEl ? numEl.textContent.replace(/\s+/g, "") : "", hit = null;
    M.forEach(function (d) {
      if (hit || d.m !== nm.textContent) return;
      if (!want || want === [b1(d.s), b1(d.g), b1(d.tu), b1(d.f)].join("/")) hit = d;
    });
    if (hit) out.push({ d: hit, thr: g, tok: slug(hit) });
  });
  return out;
}

function addDisc(d) {
  var slots = bagSlots(), s = slug(d), had = false;
  var have = slots.map(function (x) { return x.tok; })
    .filter(function (t) { return base(t) === s ? (had = true, false) : true; });
  if (!had) have.push(s);
  var h = location.hash.replace(/^#/, ""),
      keep = h.split("&").filter(function (p) { return p && p.indexOf("d=") !== 0; });
  location.hash = (have.length ? ["d=" + have.join(",")] : []).concat(keep).join("&");
}

var TIP = document.getElementById("fdsTip");
function showTip(ev, html) {
  if (!TIP) return;
  TIP.innerHTML = html; TIP.style.left = ev.clientX + "px";
  TIP.style.top = ev.clientY + "px"; TIP.style.opacity = "1";
}
function hideTip() { if (TIP) TIP.style.opacity = "0"; }

/* ---- what the map can tell you ----------------------------------------
   The verdict is VIEW-AWARE. Telling someone who only throws Kastaplast that
   they have 9 gaps, when Kastaplast makes moulds for only 11 of the 18 cells,
   is punishing them for their brand's catalogue. So coverage is always
   measured against what is actually reachable in the current view, and the
   forehand contribution is called out separately -- because "you can fill
   that with a forehand" is the single most useful thing this map knows. */
function reachable(pool) {
  var cells = {};
  pool.forEach(function (d) {
    var b = BAND[bandOf(d)], ft = lateralFt(d, 1);
    cells[b.k + "|" + colOf(b, ft)] = 1;
    cells[b.k + "|" + colOf(b, -ft)] = 1;
  });
  return cells;
}
function verdict(slots, pool) {
  var bh = {}, any = {};
  slots.forEach(function (s) {
    var b = BAND[bandOf(s.d)], i = colOf(b, lateralFt(s.d, mirrorOf(s.thr)));
    any[b.k + "|" + i] = 1;
    if (mirrorOf(s.thr) === 1) bh[b.k + "|" + i] = 1;
  });
  var canReach = reachable(pool), total = 0;
  for (var k in canReach) total++;
  var drawn = BANDS.reduce(function (n, b) { return n + b.cols; }, 0);
  var n = 0, fhOnly = 0, gaps = [];
  BANDS.forEach(function (b) {
    var half = (b.cols - 1) / 2;
    for (var i = -half; i <= half; i++) {
      var key = b.k + "|" + i;
      if (any[key]) { n++; if (!bh[key]) fhOnly++; }
      else if (canReach[key]) gaps.push({ b: b, i: i });
    }
  });
  return { n: n, total: Math.min(total, drawn), drawn: drawn, fhOnly: fhOnly, gaps: gaps };
}

/* ---- the cell detail panel -------------------------------------------
   Zoom cannot solve coincident points -- 26 discs at the same coordinate are
   still 26 discs at the same coordinate, just bigger. A list can. */
var PANEL = null, panelSort = "speed";
function closePanel() { if (PANEL) { PANEL.hidden = true; PANEL.innerHTML = ""; } }

function openCell(b, i, pool, inBag) {
  PANEL = PANEL || document.getElementById("fdsGridPanel"); if (!PANEL) return;
  var gmir = mirrorOf(globalThrow());
  var members = pool.filter(function (d) {
    if (bandOf(d) !== b.k) return false;
    var ft = lateralFt(d, gmir);
    return colOf(b, ft) === i || colOf(b, -ft) === i;
  });
  var SORTS = {
    speed: function (p, q) { return q.s - p.s || p.m.localeCompare(q.m); },
    brand: function (p, q) { return p.v.localeCompare(q.v) || p.m.localeCompare(q.m); },
    name:  function (p, q) { return p.m.localeCompare(q.m); },
    shape: function (p, q) { return lateralFt(p, gmir) - lateralFt(q, gmir); }
  };
  members.sort(SORTS[panelSort] || SORTS.speed);

  var label = COLLBL[i] || (i < 0 ? "FINISHES LEFT" : "FINISHES RIGHT");
  var h = '<div class="fdsfc-gp__hd"><div><b>' + esc(b.lbl) + " &middot; " + esc(label) + "</b>" +
    "<span>" + members.length + " disc" + (members.length === 1 ? "" : "s") +
    (i === 0 ? " &middot; finishes dead straight"
             : " &middot; finishes about " + Math.abs(Math.round(i * COL_FT)) +
               " ft " + COLPHRASE[i]) + "</span></div>" +
    '<button type="button" class="fdsfc-gp__x" aria-label="Close">&times;</button></div>' +
    '<div class="fdsfc-gp__sort" role="group" aria-label="Sort">';
  ["speed", "brand", "name", "shape"].forEach(function (s) {
    h += '<button type="button" data-sort="' + s + '" aria-pressed="' + (panelSort === s) + '">' + s + "</button>";
  });
  h += '</div><div class="fdsfc-gp__list">';
  members.forEach(function (d) {
    var mine = inBag[slug(d)], ft = lateralFt(d, gmir), fhOnly = colOf(b, ft) !== i;
    h += '<button type="button" class="fdsfc-gp__row" data-slug="' + esc(slug(d)) + '"' +
      (mine ? ' data-mine' : "") + '><i>' + (mine ? "&#10003;" : "+") + "</i>" +
      "<b>" + esc(d.m) + "</b><span>" + esc(d.v) + "</span>" +
      "<em>" + b1(d.s) + " / " + b1(d.g) + " / " + b1(d.tu) + " / " + b1(d.f) + "</em>" +
      (fhOnly ? '<u title="reaches this cell thrown the other way">forehand</u>' : "<u></u>") +
      "</button>";
  });
  h += "</div>";
  PANEL.innerHTML = h; PANEL.hidden = false;
  PANEL.querySelector(".fdsfc-gp__x").addEventListener("click", closePanel);
  Array.prototype.forEach.call(PANEL.querySelectorAll(".fdsfc-gp__sort button"), function (btn) {
    btn.addEventListener("click", function () { panelSort = btn.getAttribute("data-sort"); openCell(b, i, pool, inBag); });
  });
  Array.prototype.forEach.call(PANEL.querySelectorAll(".fdsfc-gp__row"), function (btn) {
    btn.addEventListener("click", function () { addDisc(BY[btn.getAttribute("data-slug")]); });
  });
  PANEL.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

/* ---- render ---- */
function yFor(b, y0, s) {
  var t = (b.hi - s) / (b.hi - b.lo);
  if (t < 0) t = 0; if (t > 1) t = 1;
  return y0 + PAD + t * (ROWH - 2 * PAD);
}

function render() {
  var host = document.getElementById("fdsGrid"); if (!host) return;
  var slots = bagSlots(), inBag = {}, gthr = globalThrow();
  slots.forEach(function (s) { inBag[slug(s.d)] = 1; });

  /* The pool is whatever the current view shows. Brand filtering narrows it,
     and every number on this map is measured against the pool, not against
     some abstract catalogue the viewer cannot buy from. */
  var pool = M.filter(function (d) {
    if (d.s === null || d.s === void 0 || d.tu === null) return false;
    return !brand || d.v === brand;
  });
  /* Every unbagged mould is drawn for the CURRENTLY SELECTED throw. Without
     this the labels would be lying the moment someone picked a forehand:
     the dots would still be laid out for a right-hand backhand while the
     column above them said "finishes left". */
  var gmir = mirrorOf(globalThrow());

  var v = verdict(slots, pool), vh = document.getElementById("fdsGridVerdict");
  if (vh) {
    if (!slots.length) {
      vh.innerHTML = '<b class="fdsfc-gv__n">EMPTY BAG</b><span class="fdsfc-gv__s">' +
        "Add a disc and this map shows which shots you can already throw, and which you cannot.</span>";
    } else {
      var s2 = "You cover " + v.n + " of the " + v.total + " shots on this map.";
      if (v.fhOnly) s2 += " " + v.fhOnly + " of them only on a forehand.";
      if (v.gaps.length) s2 += " Nothing that goes " + v.gaps.slice(0, 3).map(function (g) {
        return COLPHRASE[g.i] + " in the " + g.b.lbl.toLowerCase() + " band";
      }).join(", nothing that goes ") + ".";
      vh.innerHTML = '<b class="fdsfc-gv__n">' + v.n + " OF " + v.total + " SHOTS</b>" +
        '<span class="fdsfc-gv__s">' + esc(s2) + "</span>";
    }
  }

  var H = TOP + BANDS.length * ROWH + 18;
  var svg = el("svg", { viewBox: "0 0 1000 " + H, role: "img",
    "aria-label": "Shot map: how far each disc finishes offline, by disc speed" });

  /* NO OUTLINE. THE DOTS ARE THE SHAPE.
     Three goes at drawing a rim around this map, each one worse than the last.
     Boxes per row read as four stacked trays. One path with chamfered steps
     read as three cards. One path with a diagonal was lopsided, because each
     side was walked separately and the taper landed in a different row on
     each. Fixing the symmetry only made the remaining problem obvious: the
     top of the funnel is DEAD VERTICAL, because the distance and fairway rows
     genuinely reach the full width, and a rim that goes straight down and then
     kinks looks like a mistake even though it is the truth.

     So there is no rim. The dot field already draws the funnel -- the putter
     row is visibly narrower than the midrange row because its discs cannot get
     out there, which is the whole point, and it says it without a line that
     has to be argued about. The band rules stay: they are drawn only as wide
     as their own row, so they mark the taper too, and without them the four
     speed bands run together. Nothing here is clipped any more either, so no
     dot can ever be cut in half by a rim that disagrees with it.

     If a rim ever comes back, it needs the columns to step by exactly one per
     row (9/7/5/3 -- which the data does support: distance reaches +/-4,
     fairway +/-3, midrange +/-2, putters +/-1). That is the only arrangement
     where a single straight line per side contains every cell. It costs
     column width and it needs new headers for the outer two pairs. */

  /* column headers, drawn only across the widest row */
  for (var i = -2; i <= 2; i++) {
    var t = el("text", { x: colX(null, i), y: 30, "text-anchor": "middle", class: "fdsfc-gzlbl" });
    t.textContent = COLLBL[i]; svg.appendChild(t);
    var ft = el("text", { x: colX(null, i), y: 44, "text-anchor": "middle", class: "fdsfc-gzft" });
    ft.textContent = i === 0 ? "" : Math.abs(Math.round(i * COL_FT)) + " ft"; svg.appendChild(ft);
  }

  var labels = [], mineDots = [];

  BANDS.forEach(function (b, bi) {
    var y0 = TOP + bi * ROWH, mid = y0 + ROWH / 2, half = (b.cols - 1) / 2;

    /* THE ROW'S OWN WIDTH. Cells exist only where this band can actually
       reach, so fx/fw span the putter row's three columns and the midrange
       row's five. The band rule below is drawn to that width and no further,
       which is what marks the taper now that there is no rim. */
    var fx = colX(b, -half) - COLW / 2, fw = b.cols * COLW;
    if (bi) svg.appendChild(el("line", { x1: fx + 10, y1: y0, x2: fx + fw - 10, y2: y0,
      class: "fdsfc-grule" }));
    var l1 = el("text", { x: 0, y: mid - 2, class: "fdsfc-gband" }); l1.textContent = b.lbl.toUpperCase(); svg.appendChild(l1);
    var l2 = el("text", { x: 0, y: mid + 15, class: "fdsfc-gsub" }); l2.textContent = b.sub; svg.appendChild(l2);

    /* clickable cell hit areas, behind the dots */
    for (var c2 = -half; c2 <= half; c2++) (function (ci) {
      var r = el("rect", { x: colX(b, ci) - COLW / 2, y: y0, width: COLW, height: ROWH,
        class: "fdsfc-gcell", tabindex: 0, role: "button" });
      r.addEventListener("click", function () { hideTip(); openCell(b, ci, pool, inBag); });
      r.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openCell(b, ci, pool, inBag); } });
      svg.appendChild(r);
    })(c2);

    svg.appendChild(el("line", { x1: colX(b, 0), y1: y0 + 4, x2: colX(b, 0), y2: y0 + ROWH - 4, class: "fdsfc-gmid" }));

    /* ---- cluster: coincident moulds become ONE marker with a count ---- */
    var rows = pool.filter(function (d) { return bandOf(d) === b.k; });
    var clusters = {};
    rows.forEach(function (d) {
      var ft = lateralFt(d, gmir), ci = colOf(b, ft);
      var key = ci + ":" + Math.round(ft / 4) + ":" + Math.round(d.s * 2);
      (clusters[key] = clusters[key] || { ci: ci, ft: 0, s: 0, n: 0, ds: [] });
      var C = clusters[key];
      C.ds.push(d); C.n++; C.ft += ft; C.s += d.s;
    });

    Object.keys(clusters).forEach(function (key) {
      var C = clusters[key], ft = C.ft / C.n, sp = C.s / C.n;
      var cx = colX(b, 0) + (ft / COL_FT) * COLW, cy = yFor(b, y0, sp);
      var lim = colX(b, C.ci);
      if (cx < lim - COLW / 2 + 4) cx = lim - COLW / 2 + 4;
      if (cx > lim + COLW / 2 - 4) cx = lim + COLW / 2 - 4;
      var r = C.n > 1 ? Math.min(11, 3.4 + Math.sqrt(C.n) * 1.5) : 2.8;
      var g = el("g", { class: "fdsfc-gcl" });
      g.appendChild(el("circle", { cx: cx, cy: cy, r: r, class: "fdsfc-gdot" }));
      if (C.n > 1) {
        var n = el("text", { x: cx, y: cy + 3.2, "text-anchor": "middle", class: "fdsfc-gcount" });
        n.textContent = C.n; g.appendChild(n);
      }
      g.addEventListener("mousemove", function (ev) {
        showTip(ev, C.n === 1
          ? "<b>" + esc(C.ds[0].m) + "</b> &nbsp;<i>" + esc(C.ds[0].v) + "</i><br><i>" +
            b1(C.ds[0].s) + " / " + b1(C.ds[0].g) + " / " + b1(C.ds[0].tu) + " / " + b1(C.ds[0].f) +
            "</i><br><i>tap to add to your bag</i>"
          : "<b>" + C.n + " discs here</b><br><i>" +
            esc(C.ds.slice(0, 3).map(function (d) { return d.m; }).join(", ")) +
            (C.n > 3 ? " and " + (C.n - 3) + " more" : "") + "</i><br><i>tap to see them all</i>");
      });
      g.addEventListener("mouseleave", hideTip);
      g.addEventListener("click", function (ev) {
        ev.stopPropagation(); hideTip();
        if (C.n === 1) addDisc(C.ds[0]); else openCell(b, C.ci, pool, inBag);
      });
      svg.appendChild(g);
    });

    /* ---- the bag, drawn per SLOT so a mould thrown both ways appears twice ---- */
    slots.forEach(function (sl) {
      if (bandOf(sl.d) !== b.k) return;
      var mir = mirrorOf(sl.thr), ft = lateralFt(sl.d, mir), ci = colOf(b, ft);
      var cx = colX(b, 0) + (ft / COL_FT) * COLW, cy = yFor(b, y0, sl.d.s);
      var lim = colX(b, ci);
      if (cx < lim - COLW / 2 + 4) cx = lim - COLW / 2 + 4;
      if (cx > lim + COLW / 2 - 4) cx = lim + COLW / 2 - 4;
      var c = el("circle", { cx: cx, cy: cy, r: 5.6, class: "fdsfc-gdot fdsfc-gdot--mine" });
      if (mir === -1) c.setAttribute("data-fh", "");
      c.addEventListener("mousemove", function (ev) {
        showTip(ev, "<b>" + esc(sl.d.m) + "</b> &nbsp;<i>" + esc(sl.thr) + "</i><br><i>" +
          (Math.abs(ft) < 6 ? "finishes dead straight"
            : "finishes about " + Math.abs(Math.round(ft)) + " ft " + (ft < 0 ? "left" : "right")) +
          "</i><br><i>in your bag - tap to remove</i>");
      });
      c.addEventListener("mouseleave", hideTip);
      c.addEventListener("click", function (ev) { ev.stopPropagation(); hideTip(); addDisc(sl.d); });
      mineDots.push(c);
      /* Only tag a slot when it is thrown DIFFERENTLY from everything else.
         When the whole map is set to forehand, marking every disc "FH" says
         nothing -- the tag has to mean "this one is the exception". */
      labels.push({ t: sl.d.m + (sl.thr !== gthr ? " " + (mir === -1 ? "FH" : "BH") : ""),
                    x: cx, y: cy, w: sl.d.m.length });
    });
  });

  mineDots.forEach(function (c) { svg.appendChild(c); });

  labels.sort(function (p, q) { return p.x - q.x; });
  for (var li = 1; li < labels.length; li++) {
    if (labels[li].x - labels[li - 1].x < 52 && Math.abs(labels[li].y - labels[li - 1].y) < 13)
      labels[li].y = labels[li - 1].y + 13;
  }
  labels.forEach(function (L) {
    /* A label centred on a dot at the far edge runs off the canvas -- "Max FH"
       was being cut in half. Nudge it inward by roughly half its own width
       rather than clipping it. */
    var halfW = L.t.length * 3.6, x = L.x, anchor = "middle";
    if (x - halfW < 4) { x = 4; anchor = "start"; }
    else if (x + halfW > 996) { x = 996; anchor = "end"; }
    var t = el("text", { x: x, y: L.y - 10, "text-anchor": anchor, class: "fdsfc-gname" });
    t.textContent = L.t; svg.appendChild(t);
  });

  host.innerHTML = ""; host.appendChild(svg);
}

/* ---- shared with the disc picker -------------------------------------
   The picker needs to answer "does this disc fill a hole in my bag?", which
   is the map's question. Rather than reimplement the geometry over there and
   let the two drift, the map publishes the two functions that matter. If the
   funnel changes, both change together. */
window.__FDS_MAP__ = {
  cellOf: function (d, thr) {
    var b = BAND[bandOf(d)];
    return { band: b.k, col: colOf(b, lateralFt(d, mirrorOf(thr || globalThrow()))) };
  },
  /* every (band, column) the current bag does NOT cover, given that a disc can
     be thrown either way */
  gaps: function () {
    var slots = bagSlots(), have = {};
    slots.forEach(function (s) {
      var b = BAND[bandOf(s.d)];
      have[b.k + "|" + colOf(b, lateralFt(s.d, mirrorOf(s.thr)))] = 1;
    });
    return have;
  },
  fillsAGap: function (d, have) {
    var b = BAND[bandOf(d)], ft = lateralFt(d, 1);
    return !have[b.k + "|" + colOf(b, ft)] || !have[b.k + "|" + colOf(b, -ft)];
  },
  bandOf: bandOf,
  label: function (i) { return COLLBL[i]; },

  /* Ryan asked for the brand rail beside this map too, "so then we can have
     the chart populated or unpopulated". Narrowing the pool is what makes
     that switch real: the silhouette, the dot field and the "you cover N of
     M shots" verdict are all measured against the pool, so filtering to
     Innova genuinely answers "which shots can I throw out of one brand's
     catalogue" rather than dimming dots on an unchanged map.

     The BAG is deliberately not filtered. Someone comparing brands still
     wants to see what they already throw sitting on the map -- hiding their
     own discs because they happen to be Discraft would make the comparison
     useless. */
  setBrand: function (v) { brand = v || ""; render(); },
  brand: function () { return brand; },
  brands: function () {
    var n = {};
    M.forEach(function (d) {
      if (d.s !== null && d.s !== void 0 && d.tu !== null) n[d.v] = (n[d.v] || 0) + 1;
    });
    return n;
  },
  poolSize: function () {
    return M.filter(function (d) {
      if (d.s === null || d.s === void 0 || d.tu === null) return false;
      return !brand || d.v === brand;
    }).length;
  }
};

/* ---- SNAP readout. Spin, not effort. Reports the effect, not the setting. ---- */
function snap() {
  var out = document.getElementById("fdsSnapOut"), sl = document.getElementById("fdsThrottle");
  if (!out || !sl) return;
  var v = parseFloat(sl.value) || 0;
  var hero = document.querySelector(".fdsfc-hero__nm"), nm = hero ? hero.textContent : "";
  if (!v) out.textContent = "YOUR NORMAL SNAP";
  else if (v > 0) out.innerHTML = "<b>MORE SNAP</b> — " + (nm ? esc(nm) + " holds" : "discs hold") + " the line longer before fading";
  else out.innerHTML = "<b>LESS SNAP</b> — " + (nm ? esc(nm) + " fades" : "discs fade") + " out earlier and lands short";
}

/* The rail is mounted by the map rather than by brands.js, because the map is
   what knows where it belongs and what it should say when it changes. */
function mountBrandRail() {
  var host = document.getElementById("fdsMapBrands");
  if (!host || !window.__FDS_BRANDS__) return;
  var out = document.createElement("p");
  out.className = "fdsfc-mapbr__out";
  out.setAttribute("aria-live", "polite");

  function say() {
    var n = window.__FDS_MAP__.poolSize();
    out.innerHTML = brand
      ? "Showing <b>" + esc(brand) + "</b> &mdash; <b>" + n + "</b> mould" + (n === 1 ? "" : "s") +
        " on the map. Your bag stays on it whatever brand you pick."
      : "Showing every brand &mdash; <b>" + n + "</b> moulds.";
  }

  window.__FDS_BRANDS__.mount(host, {
    allLabel: "Every brand",
    note: out,
    onPick: function (v) { window.__FDS_MAP__.setBrand(v); say(); }
  });
  say();
}

function boot() {
  render(); snap(); mountBrandRail();
  var sl = document.getElementById("fdsThrottle");
  if (sl) { sl.addEventListener("input", snap); sl.addEventListener("dblclick", function () { setTimeout(snap, 0); }); }
  var slots = document.getElementById("fdsSlots");
  if (slots && window.MutationObserver) {
    new MutationObserver(function () { render(); snap(); }).observe(slots, { childList: true, subtree: true });
  }
  window.addEventListener("hashchange", function () { setTimeout(function () { render(); snap(); }, 0); });
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
})();